import React, { useContext, useEffect, useState } from 'react'

import theme from 'src/utils/styles/theme'
import { graphql, useStaticQuery } from 'gatsby'
import { renderRichText } from 'gatsby-source-contentful/rich-text'
import { useLocation } from '@reach/router'
import StoreContext from '../../context/StoreContext'
import { Container, Text, BannerContent, HolidayImage } from './styles'

import {
  IS_FOREIGN_COUNTRY,
  LOCALIZATION,
  IN_AU,
  IN_CA,
  IN_SEASONAL_STATE,
  VIP_CODE,
  CURRENT_SEASONAL_STATE,
  SEASONAL_STATES,
  CAL_MAX_ON_SALE,
} from '../../utils/constants'

import { bannerHTMLFormatter } from './utils'
import { RichText } from '../Contentful/atoms/RichText'

function getUtmParameter(paramName) {
  const urlParams = new URLSearchParams(window.location.search)
  return urlParams.get(paramName)
}

const Banner = () => {
  const { holidayBannerMobile, bannerV2, seasonalBanner } = useStaticQuery(graphql`
    query BannerV2Query {
      holidayBannerMobile: file(relativePath: { eq: "giftGuide/holiday-banner-mobile.webp" }) {
        childImageSharp {
          gatsbyImageData(quality: 100, placeholder: NONE)
        }
      }
      bannerV2: allContentfulBannerV2 {
        edges {
          node {
            color
            textColor
            influencerColor
            influencerTextColor
            en {
              raw
            }
            en_au: enAu {
              raw
            }
            en_ca: enCa {
              raw
            }
            en_calendar: enCalendar {
              raw
            }
            en_gb: enGb {
              raw
            }
          }
        }
      }
      seasonalBanner: allContentfulSeasonalBanner {
        edges {
          node {
            vip_bkgdColor
            vip_textColor
            vip_linkColor
            bfcmCore_bkgdColor
            bfcmCore_textColor
            bfcmCore_linkColor
            bfcmLastChance_bkgdColor
            bfcmLastChance_textColor
            bfcmLastChance_linkColor
            en_vip_mainText {
              raw
            }
            en_ca_vip_mainText {
              raw
            }
            en_au_vip_mainText {
              raw
            }
            en_gb_vip_mainText {
              raw
            }
            en_bfcmCore_mainText {
              raw
            }
            en_ca_bfcmCore_mainText {
              raw
            }
            en_au_bfcmCore_mainText {
              raw
            }
            en_gb_bfcmCore_mainText {
              raw
            }
            en_bfcmCore_calMaxText {
              raw
            }
            en_ca_bfcmCore_calMaxText {
              raw
            }
            en_au_bfcmCore_calMaxText {
              raw
            }
            en_gb_bfcmCore_calMaxText {
              raw
            }
            en_bfcmLastChance_mainText {
              raw
            }
            en_ca_bfcmLastChance_mainText {
              raw
            }
            en_au_bfcmLastChance_mainText {
              raw
            }
            en_gb_bfcmLastChance_mainText {
              raw
            }
            en_bfcmLastChance_calMaxText {
              raw
            }
            en_ca_bfcmLastChance_calMaxText {
              raw
            }
            en_au_bfcmLastChance_calMaxText {
              raw
            }
            en_gb_bfcmLastChance_calMaxText {
              raw
            }
            en_vip_frameText {
              raw
            }
            en_ca_vip_frameText {
              raw
            }
            en_au_vip_frameText {
              raw
            }
            en_gb_vip_frameText {
              raw
            }
            en_bfcmCore_frameText {
              raw
            }
            en_ca_bfcmCore_frameText {
              raw
            }
            en_au_bfcmCore_frameText {
              raw
            }
            en_gb_bfcmCore_frameText {
              raw
            }
            en_bfcmLastChance_frameText {
              raw
            }
            en_ca_bfcmLastChance_frameText {
              raw
            }
            en_au_bfcmLastChance_frameText {
              raw
            }
            en_gb_bfcmLastChance_frameText {
              raw
            }
          }
        }
      }
    }
  `)

  const [showBanner, setShowBanner] = useState(false)
  const [discountApplied, setDiscountApplied] = useState(false)

  const {
    store: {
      cart,
      banner: { promoCode, channel, amount },
    },
    applyDiscount,
    removeDiscount,
    updateBanner,
  } = useContext(StoreContext)

  const { id } = cart
  const discountCode = cart?.discountCodes?.[0]?.code || ''

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowBanner(true)
    }, 1000)
    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    if (!id || !promoCode || discountApplied) return

    const applyOrUpdateDiscount = async () => {
      try {
        // if (discountCode && discountCode.toUpperCase() !== promoCode.toUpperCase()) {
        //   await removeDiscount(id)
        // }

        if (promoCode.toUpperCase() === 'EARLYACCESS') {
          updateBanner()
          removeDiscount()
        }

        if (discountCode !== promoCode) {
          await applyDiscount(promoCode)
          setDiscountApplied(true) // Mark as applied
        }
      } catch (error) {
        console.error('Error while applying or removing discount:', error)
      }
    }

    applyOrUpdateDiscount()
  }, [id, promoCode, discountCode, applyDiscount, removeDiscount, discountApplied])

  // Additional useEffect to reset discountApplied state when promoCode changes
  useEffect(() => {
    setDiscountApplied(false)
  }, [promoCode])

  useEffect(() => {
    const source = getUtmParameter('utm_source')
    const utm_campaign = getUtmParameter('utm_campaign')
    const utm_medium = getUtmParameter('utm_medium')
    const utm_term = getUtmParameter('utm_term')
    const utm_deal = getUtmParameter('utm_deal')
    if (source === 'babylist') {
      updateBanner('BABYLIST', 'email', '25%')
    }
    if (source === 'homeedit') {
      updateBanner('HOMEEDIT', 'email', '20%')
    }
    if (utm_campaign === 'cheer_video' || utm_campaign === 'cheer_banner') {
      updateBanner('CHEER', 'PodcastFrame', '$15')
    }
    if (utm_medium === 'Influencer' || utm_medium === 'InfluencerMax') {
      if (utm_campaign === 'HolidayCal' || utm_campaign === 'NewYear1') {
        updateBanner(utm_term, utm_campaign, utm_deal)
      } else if (utm_campaign === 'NewYear') {
        updateBanner(utm_term, utm_campaign, utm_deal)
      } else {
        updateBanner(utm_term, utm_medium, utm_deal)
      }
    }
    if (utm_campaign === 'GMA40' || utm_campaign === 'GMA40/' || utm_campaign === 'GMA40%2F')
      updateBanner('GMA40', 'GMA', '$40')

    if (utm_campaign === 'TODAY' || utm_campaign === 'TODAY/' || utm_campaign === 'TODAY%2F')
      updateBanner('TODAY', 'TODAY', '20%')

    // One off for Dual Promo Sept '25 -- check that all discount codes are expired before removing
    if (utm_campaign === 'DualPromo') {
      updateBanner(utm_term, utm_campaign, utm_deal)
    }
    if (utm_campaign === 'BFCM1' || utm_campaign === 'BFCM2') {
      updateBanner(utm_term, utm_campaign, utm_deal)
    }
  }, [])

  const MARKETING_BANNER_OVERRIDES = {
    Toast: `Thanks for listening! Use Code ${promoCode} for ${amount} off!`,
    PodcastCalendar: `Thanks for listening! Use code ${promoCode} for ${amount} off your Calendar purchase.`,
    Influencer: `We're so glad you're here! Please use ${promoCode} for ${amount} off your 15" Calendar purchase.`,
    InfluencerMax: `We're so glad you're here! Please use ${promoCode} for ${amount} off your Calendar Max.`,
    TODAY: `We're so glad you're here! Please use ${promoCode} for ${amount} off your 10" or 15" Calendar purchase.`,
    InfluencerFrame: `We're so glad you're here! Please use ${promoCode} for ${amount} off your frame purchase.`,
    PodcastFrame: `Thanks for listening! Use code ${promoCode} for ${amount} off your 10" Frame purchase!`,
    PodcastBoth: `Thanks for listening! Use code ${promoCode} for ${amount} off!`,
    GMA: `We're so glad you're here! Please use GMA40 for $40 off your 10" or 15" Calendar purchase.`,
    DirectMail: `Use code ${promoCode} for ${amount} off your 15" Calendar.`,
    PR: `We're so glad you're here. Use ${promoCode} for ${amount} off your 10" or 15" Calendar purchase.`,
    TVFrame: `Use code TV20 FOR $20 OFF your Frame at checkout`,
    // TVCal: `${
    //   IS_FOREIGN_COUNTRY
    //     ? 'Use code TV FOR 20% OFF your Frame at checkout'
    //     : 'Use code TV15 FOR 15% OFF your Calendar at checkout'
    // }`,
    InfluencerFrame2: `We're so glad you're here! Please use ${promoCode} for ${amount} off your Frame 2 purchase.`,
    StreamingCalendar: `We're so glad you're here! Please use ${promoCode} for  ${amount} off your 15" Calendar purchase.`,
    DualPromo: `We're so glad you're here! Please use ${promoCode} for ${amount} off any Calendar purchase.`,
    Holiday: `We're so glad you're here! Please use ${promoCode} for ${amount} off any Frame purchase.`,
    FrameHoliday: `We're so glad you're here! Please use ${promoCode} for ${amount} off any Frame purchase.`,
    HolidayCal: `We're so glad you're here! Please use ${promoCode} for ${amount} off your Calendar purchase.`,
    NewYear1: `We're so glad you're here! Please use ${promoCode} for ${amount} off your Calendar purchase.`,
    NewYear: `✨ Start the year off right! Use code ${promoCode} for ${amount} off Calendar.✨`,
    MDFrame: `The Best Mother's Day Gift! Please use ${promoCode} to save on your Frame purchase.`,
  }

  const discountMessages = {
    BABYLIST:
      'Cyber Week Sale ✨ Save $30 off select Skylight Frames. BABYLIST discount code automatically applied at checkout.',
    CHEER: 'Take $15 off your purchase with the code CHEER',
    HOMEEDIT:
      'Enjoy 20% off your Skylight Calendar purchase. Discount automatically applied at checkout.',
    CASEFILE: `Thanks for listening! Use code CASEFILE for ${
      IN_AU ? '20%' : '15%'
    } off your Frame purchase!`,
    MFM: `Thanks for listening! Use code MFM for ${
      IN_AU || IN_CA ? '20%' : '15%'
    } off your Frame purchase!`,
    LOVE: `Thanks for listening! Use code LOVE for ${
      IN_CA ? '20%' : '$20'
    } off your Frame purchase!`,
    FUN: `Thanks for listening! Use code FUN for ${IN_CA ? '20%' : '$20'} off your Frame purchase!`,
    BFCM1: `We're so glad you're here! Use code ${promoCode} for $40 off any 15" or 27" Calendar purchase`,
    BFCM2: `Biggest savings of the year! Use code ${promoCode} to save big on your 15” or 27” Calendar purchase!`,
  }
  let backgroundColor = theme[bannerV2?.edges?.[0]?.node?.color] || theme.blueDark

  if (bannerV2?.edges?.[0]?.node?.color === 'holiday') {
    backgroundColor = 'holiday'
  }

  let textColor = theme[bannerV2?.edges?.[0]?.node?.textColor] || 'white'
  let linkColor = theme[bannerV2?.edges?.[0]?.node?.linkColor] || theme.coral
  if (bannerV2?.edges?.[0]?.node?.color === 'holiday') {
    linkColor = '#fff'
  }
  let influencerBackgroundColor =
    theme[bannerV2?.edges?.[0]?.node?.influencerColor] || theme.lightLavender
  let influencerTextColor = theme[bannerV2?.edges?.[0]?.node?.influencerTextColor] || theme.emerald
  let influencerLinkColor = theme[bannerV2?.edges?.[0]?.node?.influencerLinkColor] || theme.emerald
  if (bannerV2?.edges?.[0]?.node?.influencerColor === 'holiday') {
    influencerBackgroundColor = 'holiday'
    influencerTextColor = '#fff'
    influencerLinkColor = '#fff'
  }

  const { pathname } = useLocation()

  const [bannerText, setBannerText] = useState(null)
  const isCalendarPage = pathname.includes('calendar')
  const isCalendarMaxPage = pathname.includes('calendar-max')
  const isFramePage = pathname.includes('frame')
  const isAmazonPage = pathname.includes('amazon')

  useEffect(() => {
    const BANNER_COPY = bannerV2.edges[0].node
    const normalizedLocalization = LOCALIZATION.replace('-', '_')
    if (
      isCalendarPage &&
      !IS_FOREIGN_COUNTRY &&
      renderRichText(BANNER_COPY[normalizedLocalization])?.[0]?.props?.children?.[0] !== 'NONE'
    ) {
      setBannerText(BANNER_COPY.en_calendar)
    } else if (
      renderRichText(BANNER_COPY[normalizedLocalization])?.[0]?.props?.children?.[0] !== 'NONE'
    ) {
      setBannerText(BANNER_COPY[normalizedLocalization])
    } else {
      setBannerText(null)
    }
  }, [pathname, bannerV2])

  const isInBFCMState = IN_SEASONAL_STATE || promoCode === VIP_CODE
  const seasonalBannerData = seasonalBanner?.edges[0]?.node
  const currentState = IN_SEASONAL_STATE ? CURRENT_SEASONAL_STATE : SEASONAL_STATES.vip // Conditional logic below prevents this from having any effect if !inBFCMState

  if (isInBFCMState) {
    if (!SEASONAL_STATES[CURRENT_SEASONAL_STATE])
      console.warn('Current seasonal state envVar is not valid - using banner fallbacks')
    backgroundColor = seasonalBannerData[`${currentState}_bkgdColor`] || theme.space
    textColor = seasonalBannerData[`${currentState}_textColor`] || theme.white
    linkColor = seasonalBannerData[`${currentState}_linkColor`] || theme.coral
  }

  useEffect(() => {
    if (!isInBFCMState) return
    const normalizedLocalization = LOCALIZATION.replace('-', '_')
    const mainCopy = seasonalBannerData[`${normalizedLocalization}_${currentState}_mainText`]
    const fallbackCopy = 'Get our best deals on Calendar and Frame!'

    if (isCalendarMaxPage && CAL_MAX_ON_SALE) {
      const bannerCopy =
        seasonalBannerData[`${normalizedLocalization}_${currentState}_calMaxText`] ?? mainCopy

      const finalCMCopy = bannerCopy ?? fallbackCopy

      return setBannerText(finalCMCopy)
    }

    if (isFramePage) {
      const bannerCopy =
        seasonalBannerData[`${normalizedLocalization}_${currentState}_frameText`] ?? mainCopy

      const finalFrameCopy = bannerCopy ?? fallbackCopy
      return setBannerText(finalFrameCopy)
    }

    const finalMainCopy = mainCopy ?? fallbackCopy
    return setBannerText(finalMainCopy)
  }, [isInBFCMState, seasonalBannerData, isCalendarMaxPage, isFramePage, currentState, pathname])

  if (isAmazonPage) return null
  if (
    promoCode &&
    promoCode !== 'MDVIP' &&
    promoCode !== 'BEKAHMD' &&
    promoCode !== 'CALM' &&
    promoCode !== 'MAXREADY' &&
    promoCode !== 'MAXPREP' &&
    promoCode !== 'MAXBTS' &&
    promoCode !== 'VIPBTS' &&
    promoCode !== `${VIP_CODE}` &&
    channel !== 'BFCM1' &&
    channel !== 'BFCM2' &&
    promoCode !== 'MDVIP26'
  ) {
    return (
      <Container
        // bgColor={
        //   influencerBackgroundColor === 'holiday' ? 'transparent' : influencerBackgroundColor
        // }
        // bgColor="#FCDFDF"
        bgColor="#C1EBEE"
        linkColor={influencerLinkColor}
        className="banner"
      >
        {influencerBackgroundColor === 'holiday' && (
          <HolidayImage
            image={holidayBannerMobile.childImageSharp.gatsbyImageData}
            alt="Holiday Themed background"
          />
        )}

        <Text textcolor={influencerTextColor}>
          {showBanner && (
            <>{MARKETING_BANNER_OVERRIDES[channel] || bannerHTMLFormatter(promoCode, amount)}</>
          )}
        </Text>
      </Container>
    )
  }

  if (discountMessages[promoCode] || discountMessages[channel]) {
    return (
      <Container bgColor={backgroundColor} className="banner">
        <Text>{showBanner && <>{discountMessages[promoCode] || discountMessages[channel]}</>}</Text>
      </Container>
    )
  }
  if (bannerText) {
    const finalTextColor = theme[textColor]
    const finalLinkColor = theme[linkColor]
    return (
      <Container
        bgColor={backgroundColor}
        boldSpanColor={finalTextColor}
        // boldSpanUnderline={!isInBFCMState}
        textColor={finalTextColor}
        className="banner"
      >
        {backgroundColor === 'holiday' && (
          <HolidayImage
            image={holidayBannerMobile.childImageSharp.gatsbyImageData}
            alt="Holiday Themed background"
          />
        )}
        <BannerContent textColor={finalTextColor} linkColor={finalLinkColor}>
          <RichText content={bannerText} />
        </BannerContent>
      </Container>
    )
  }

  return null
}

export default Banner
